/**
 * Helpers for shaping volume discounts into the PriceListPdf row format.
 *
 * Volume discounts are an absolute selling price at a minimum quantity
 * (column: price_per_kg). The displayed "% off" is computed against the
 * wholesale standard-grade catalogue price — the rationale being that
 * volume discounts only kick in once a customer is on the wholesale tier
 * (via tier or via threshold upgrade), so wholesale is the meaningful
 * "previous" price. When the volume price equals that baseline, we omit
 * the % suffix and show just £/kg.
 */

/**
 * Find the standard grade in a pdfGroup (multiplier closest to 1.0).
 * pdfGroup shape: { product_name, grades: [{ grade_name, multiplier, tiers: { [tierName]: price } }] }
 */
export function findStandardGrade(group) {
  if (!group?.grades?.length) return null
  return group.grades.reduce((best, g) => {
    if (!best) return g
    return Math.abs((g.multiplier ?? 1) - 1) < Math.abs((best.multiplier ?? 1) - 1) ? g : best
  }, null)
}

/**
 * Shape a raw volume_discount row for PriceListPdf. Uses price_per_kg as the
 * canonical selling price (falls back to deriving it from discount_percent
 * for legacy rows). The displayed discount % is recomputed against the
 * wholesale standard-grade catalogue baseline.
 *
 * @param {object} d         Raw volume_discounts row (with product, price_tier joins).
 * @param {Array}  pdfGroups Grouped catalogue for the PDF (used to look up baseline).
 * @returns {object|null}    Row in PriceListPdf shape, or null if no useful price.
 */
export function buildVolumeDiscountRow(d, pdfGroups) {
  const group = pdfGroups.find(g => g.product_name === d.product?.strain)
  const standardGrade = findStandardGrade(group)

  // Baseline: wholesale-tier × standard-grade catalogue price.
  // This is the price the customer pays at the regular wholesale quantity
  // (before volume discounts kick in), so it's the meaningful "previous" price.
  const baseline =
    standardGrade?.tiers?.['wholesale']
    ?? (d.product?.base_price_per_kg != null && d.price_tier?.multiplier != null
        ? d.product.base_price_per_kg * parseFloat(d.price_tier.multiplier) * (standardGrade?.multiplier ?? 1)
        : null)

  // Selling price: prefer the absolute price_per_kg; otherwise derive from discount_percent.
  let sellingPrice = null
  if (d.price_per_kg != null && d.price_per_kg !== '') {
    sellingPrice = Number(d.price_per_kg)
  } else if (baseline != null && d.discount_percent != null) {
    sellingPrice = baseline * (1 - Number(d.discount_percent) / 100)
  }

  const displayedPct = (baseline != null && sellingPrice != null && baseline > 0)
    ? Math.max(0, Math.round((1 - sellingPrice / baseline) * 1000) / 10)
    : (Number(d.discount_percent) || 0)

  return {
    product_name: d.product?.strain || null,
    tier_name: d.price_tier?.name,
    tier_display_name: d.price_tier?.display_name,
    min_quantity: d.min_quantity,
    discount_percent: displayedPct,
    selling_price: sellingPrice,
  }
}
